import BaseUISystem from '../BaseUISystem';
import BaseMenu from './BaseMenu';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import Game from '../../../Game';
import { IGameState } from '../../../State/Game/IGameState';
import Renderable from '../../../Component/Renderable';
import UITextComponent from '../../../Component/UI/Text';
import { UIActions } from '../../../State/UI/UIActions';
import { applyDefaultButtonAnimation } from '../UIHelper';
import UIImageComponent from '../../../Component/UI/Image';
import ElementComponent from '../../../Component/UI/Element';
import { StarChestHelper } from '../../../Utility/StarChestHelper';
import ColorFilter from '../../../Component/ColorFilter';
import { MathHelper } from '../../../Utility/MathHelper';
import { GameActions } from '../../../State/Game/GameActions';
import { getCurrentLevelNumber } from '../../../State/Player/PlayerLevelHelper';
import { tVector4 } from '../../../Utility/ArrayHelper';
import { StarChestStateHelper } from 'State/Player/StarChests/StarChestStateHelper';
import { PopupID } from '../../../State/UI/PopupID';
import { PlayerStateHelper } from 'State/Player/PlayerStateHelper';

const buttonAnchors: Record<string, tVector4> = {
    left: [0.37, 0.83, 0.37, 0.83],
    right: [0.63, 0.83, 0.63, 0.83],
    center: [0.5, 0.83, 0.5, 0.83],
};

export default class StarChestInfoHandler extends BaseMenu {
    constructor(uiSystem: BaseUISystem) {
        super(PopupID.STAR_CHEST_INFO, uiSystem);
        this.audioClipShow = 'chest-opening';
    }

    public onCreate() {
        super.onCreate();
        this.addButtonListeners();
    }
    public onShow(params: {levelNumber: number}) {
        const infoText = this.element.find('text_link');
        const collectText = this.element.find('collect_text');
        const progressBar = this.element.find('progress_bar_container');
        const chestContainer = this.element.find('chest_container');
        const claimButtonContainer = this.element.find('claim_btn');
        const continueButtonContainer = this.element.find('continue_btn');

        const state: IGameState = Game.store.getState();
        const starChestUnlockTier: number = StarChestStateHelper.getStarChestUnlockTier(
            state.playerState
        );
        const nextTierExists = StarChestHelper.tierExists(
            starChestUnlockTier + 1
        );

        const levelNumber =
            params.levelNumber ?? getCurrentLevelNumber(state.playerState);
        const atStarChestLevel = PlayerStateHelper.shouldOpenChest(
            state.playerState,
            levelNumber
        );

        setInfoText();
        setButtonsStatus();
        setChestsStatus();

        // Update progress bar
        const starChestStarCount = StarChestStateHelper.getStarChestStarCount(state.playerState);
        setProgressBar(starChestUnlockTier, starChestStarCount);
        return;
        function setButtonsStatus() {
            const claimRenderable = claimButtonContainer.getComponent(
                Renderable
            );
            const continueRnderable = continueButtonContainer.getComponent(
                Renderable
            );

            const claimElement = claimButtonContainer.getComponent(
                ElementComponent
            );
            const continueElement = continueButtonContainer.getComponent(
                ElementComponent
            );

            claimRenderable.visible = atStarChestLevel;
            continueRnderable.visible = nextTierExists || !atStarChestLevel;

            if (claimRenderable.visible && continueRnderable.visible) {
                claimElement.anchors = buttonAnchors.right;
                continueElement.anchors = buttonAnchors.left;
            } else {
                claimElement.anchors = buttonAnchors.center;
                continueElement.anchors = buttonAnchors.center;
            }
        }
        function setInfoText() {
            const infoTextComponent = infoText.getComponent(UITextComponent);
            infoTextComponent.text = atStarChestLevel
                ? Game.locale.getText('UpgradeStarChestInfoHint')
                : Game.locale
                      .getText('StarChestCollectLevelX')
                      .replace(
                          '[X]',
                          String(state.playerState.starChestLevel - 1)
                      );

            infoText.getComponent(Renderable).visible =
                nextTierExists || !atStarChestLevel;

            collectText.getComponent(UITextComponent).text = Game.locale.getText(
                atStarChestLevel ? 'StarChestOpen' : 'StarChestCollect',
            );

            collectText.getComponent(Renderable).visible =
                nextTierExists && atStarChestLevel;
        }
        /**
         * Adjusts each chest container to correctly show which ones are locked/unlocked
         */
        function setChestsStatus() {
            chestContainer.children.forEach(
                (chestContainer: GameObject, index: number) => {
                    const { requiredStars = 0, luckyTurnipPicks = [] } =
                        StarChestHelper.getChestTierData(index) ?? {};

                    setChestHighlight(
                        chestContainer,
                        starChestUnlockTier,
                        index
                    );
                    setChestUnlockRequirement(chestContainer, requiredStars);
                    setChestUnlockRewardCount(
                        chestContainer,
                        luckyTurnipPicks.length
                    );
                }
            );
            return;
            function setChestHighlight(
                chestContainer: GameObject,
                unlockedTier: number,
                tier: number
            ) {
                const unlocked = unlockedTier >= tier;
                const active = unlockedTier === tier;

                const mainContainer = chestContainer.find('main_container');
                const mainColorFilter = mainContainer.hasComponent(ColorFilter)
                    ? mainContainer.getComponent(ColorFilter)
                    : mainContainer.addComponent(ColorFilter);

                const star = chestContainer.findChildByName('star');
                if (star) {
                    const starColorFilter = star.hasComponent(ColorFilter)
                        ? star.getComponent(ColorFilter)
                        : star.addComponent(ColorFilter);
                    starColorFilter.brightness = unlocked && !active ? 0.6 : 1;
                }

                const border = mainContainer.findChildByName('chest_bg');
                const fill = mainContainer.findChildByName('chest_fill');

                border.getComponent(Renderable).visible = !unlocked;
                fill.getComponent(Renderable).visible = unlocked;

                mainColorFilter.brightness = active ? 1 : 0.6;
            }
            function setChestUnlockRequirement(
                chestContainer: GameObject,
                starRequirement: number
            ) {
                const starText = chestContainer.findChildByName('star_text');

                if (starText) {
                    starText.getComponent(UITextComponent).text = String(
                        starRequirement
                    );
                }
            }
            function setChestUnlockRewardCount(
                chestContainer: GameObject,
                rewardCount: number
            ) {
                const rewardText = chestContainer.findChildByName(
                    'reward_text'
                );

                rewardText.getComponent(UITextComponent).text = String(
                    rewardCount
                );
            }
        }
        function setProgressBar(unlockedTier: number, starCount: number) {
            const tierFrom = StarChestHelper.getCurrentOrPreviousTier(
                unlockedTier
            );
            const tierTo = StarChestHelper.getNextOrLastTier(unlockedTier);

            const tierDataFrom = StarChestHelper.getChestTierData(tierFrom);
            const tierDataTo = StarChestHelper.getChestTierData(tierTo);

            const currentChest = progressBar.find('current_icon');
            const nextChest = progressBar.find('next_icon');
            const fill = progressBar.find('progress_bar/progress_bar_fill');
            const progressText = progressBar.find('progress_bar_text');

            // Set current chest
            currentChest.getComponent(
                UIImageComponent
            ).texture = StarChestHelper.getStarChestTexture(tierFrom);

            // Set next chest
            nextChest.getComponent(
                UIImageComponent
            ).texture = StarChestHelper.getStarChestTexture(tierTo);
            nextChest.getComponentsInChildren(UITextComponent)[0].text = String(
                tierDataTo?.requiredStars ?? '??'
            );

            // Set fill scale
            const fillBarProgress = StarChestHelper.getProgressToNextStarChest(
                tierFrom,
                starCount,
                0.1,
                0.075
            );
            fill.getComponent(ElementComponent).scale.set(fillBarProgress, 1);

            const clampedStarCount = MathHelper.clamp(
                starCount,
                0,
                tierDataTo?.requiredStars ?? 0
            );
            // Set progress text
            progressText.getComponent(
                UITextComponent
            ).text = `${clampedStarCount} / ${tierDataTo?.requiredStars ?? 0}`;
        }
    }

    private readonly onClaimSelected = () => {
        this.close();
        GameActions.loadLuckyTurnips();
    };
    private readonly onContinueSelected = () => {
        this.close();
    };
    private close() {
        UIActions.hidePopup(this.name);
    }
    private addButtonListeners() {
        const claimButton = this.element.find('claim_btn/button');
        const continueButton = this.element.find('continue_btn/button');

        applyDefaultButtonAnimation(claimButton);
        claimButton.on(GameObjectEvent.SELECTED, this.onClaimSelected);
        this.cleanupManager.add(() => {
            claimButton.off(GameObjectEvent.SELECTED, this.onClaimSelected);
        });

        applyDefaultButtonAnimation(continueButton);
        continueButton.on(GameObjectEvent.SELECTED, this.onContinueSelected);
        this.cleanupManager.add(() => {
            continueButton.off(
                GameObjectEvent.SELECTED,
                this.onContinueSelected
            );
        });
    }
}
